import './tickets.scss';

import { Col, Row } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useQueryString from '../../hooks/useQueryString';

import BookingSummary from '../../components/booking-summary/booking-summary';
import Heading, { HeadingSize } from '../../components/heading/heading';
import DatePickerSection from '../../page-sections/date-picker-section';
import PackagePickerSection from '../../page-sections/package-picker-section';
import PlayerPickerSection from '../../page-sections/player-picker-section';
import TimePickerSection from '../../page-sections/time-picker-section';
import { actionCreators as PaymentActions } from '../../store/Payments';
import { actionCreators as BookingActions } from '../../store/Booking';
import { actionCreators as GiftVoucherActions, GiftVoucherStatus } from '../../store/GiftVoucher';
import { actionCreators as PurchaseTypeActions, PurchaseTypes } from '../../store/PurchaseTypes';
import { actionCreators as SiteActions } from '../../store/Sites';
import { actionCreators as GameActions } from '../../store/Games';
import { actionCreators as locationActions } from '../../store/Location';
import { actionCreators as RewardActions } from '../../store/Rewards';

import { GoogleTagManagerEvents } from '../../helpers/googleTagManager';

import { IAnalyticsAction, IApplicationState } from '../../store';
import PurchaseTypeSection from '../../page-sections/gift-voucher/purchase-type-section';

import GiftVoucherAmountSection from '../../page-sections/gift-voucher/gift-voucher-amount-section';
import GiftVoucherDetailsSection from '../../page-sections/gift-voucher/gift-voucher-details-section';
import PrivateEventTypeSection from '../../page-sections/private-event-type-section';
import { BookingAlert } from '../../components/bookingAlert/bookingAlert';
import { StickyInfoWidget } from '../../components/location-finder/LocationWidget';
import { useIsMobile } from '../../hooks/useIsMobile';
import useTieredGroupSizeDiscountPromo from '../../hooks/useTieredGroupSizeDiscountPromo';
import { usePreselectSite } from '../../hooks/usePreselectedSite';
import { Routes, navigateTo } from '../../helpers/routes';

export interface ITicketsPageProps {
    reservationTimerHeight?: number;
}

const TicketsPage: React.FunctionComponent<ITicketsPageProps> = ({ reservationTimerHeight = 0 }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isMobile } = useIsMobile();
    useTieredGroupSizeDiscountPromo();
    const params: any = useParams();
    const { params: queryStringParams, raw: rawQueryString } = useQueryString();
    const [paramsAdded, setParamsAdded] = useState(false);
    const [queryAdded, setQueryAdded] = useState(false);

    // keep track of location widget height to make sure booking summary doesn't overlap weirdly in desktop
    const [locationWidgetHeight, setLocationWidgetHeight] = useState(0);

    const purchaseSelectedUI = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseTypeUI);
    const purchaseAmountSelectedUI = useSelector(
        (state: IApplicationState) => state.giftVoucher.selectedGiftVoucherAmountUI
    );
    const siteSelectedUI = useSelector((state: IApplicationState) => state.sites.selectedSiteUI);
    const playersSelectedUI = useSelector((state: IApplicationState) => state.players.selectedPlayersUI);

    const datesLoading = useSelector((state: IApplicationState) => state.session.openDatesLoading);
    const datesLoaded = useSelector((state: IApplicationState) => state.session.openDatesLoaded);
    const dateSelectedUI = useSelector((state: IApplicationState) => state.session.selectedDateUI);

    const timesLoading = useSelector((state: IApplicationState) => state.session.sessionTimesLoading);
    const timesLoaded = useSelector((state: IApplicationState) => state.session.sessionTimesLoaded);
    const timeSelectedUI = useSelector((state: IApplicationState) => state.session.selectedTimeUI);

    const site = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const accessCode = useSelector((state: IApplicationState) => state.sites.accessCode);

    const hasGiftVoucherAvailable = useSelector(
        (state: IApplicationState) => state.sites.selectedSite?.giftVouchersAvailable
    );
    const useExternalBookingUrl = useSelector(
        (state: IApplicationState) => state.sites.selectedSite?.useExternalBookingUrl
    );
    const giftVoucher = useSelector((state: IApplicationState) => state.giftVoucher.giftVoucher);
    const giftVoucherStatus = useSelector(
        (state: IApplicationState) => state.giftVoucher.giftVoucher?.giftVoucherStatusTypeId
    );
    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const priceSets = useSelector((state: IApplicationState) => state.booking.priceSets);

    const giftVoucherAvailable =
        hasGiftVoucherAvailable && selectedPurchaseType === PurchaseTypes.GiftVoucher && !useExternalBookingUrl;

    const featureFlags = useSelector((state: IApplicationState) => state.featureFlags);

    const showUiSections = useMemo(() => {
            //if we are using the new location widget, and we haven't yet selected a venue, hide all the other ui elements
        if (!siteSelectedUI) {
            return false;
        }

        //catch all to make sure the UI doesn't enter a broken state
        return true;
    }, [siteSelectedUI]);

    useEffect(() => {
        if (!paramsAdded) {
            //apply pre-selected venue name
            //(the access code is ingested centrally in App.tsx, along with a venue fallback
            //for the routes that don't declare a :venue param)
            if (params && params.venue) {
                dispatch(SiteActions.preselectSite(params.venue));
            }

            setParamsAdded(true);
        }
    }, [dispatch, params, paramsAdded]);

    // UseEffect for aggressive location prompt
    useEffect(() => {
        //if we have handled the params, and not detected an attempt to pre select a venue
        if (paramsAdded && !params.venue) {
            if(featureFlags.hasLoaded && selectedPurchaseType === PurchaseTypes.GiftVoucher) {
                //if the user is purchasing a gift voucher, redirect to regional gift voucher page
                dispatch(navigateTo(Routes.RegionalGiftVoucher, null, rawQueryString));
            } else if (featureFlags.hasLoaded) {
                //if we are using the new location widget, auto prompt the user to share location
                dispatch(locationActions.postGetLocationAction());

                dispatch({
                    type: 'ANALYTICS_EVENT',
                    googleTagManagerEvent: GoogleTagManagerEvents.AggressiveLocationPromptTriggered(),
                } as IAnalyticsAction);
            }
        }
    }, [dispatch, params, paramsAdded, featureFlags.hasLoaded, selectedPurchaseType, rawQueryString]);


    //helper hook that will then update the selectedSite to be the 'pre selected site' value we picked from params and saved into state
    usePreselectSite();

    useEffect(() => {
        //only one code at a time
        if (!queryAdded) {
            const giftVoucherParam = process.env.REACT_APP_GIFTVOUCHER_PARAM ?? 'gv';
            const promoCodeParam = process.env.REACT_APP_PROMOCODE_PARAM ?? 'promo';
            const gameParam = process.env.REACT_APP_GAME_PARAM ?? 'game';
            const priceSetParam = 'set';

            if (queryStringParams.get(giftVoucherParam))
                dispatch(BookingActions.recordPreselectedCode(queryStringParams.get(giftVoucherParam)));
            else if (queryStringParams.get(promoCodeParam))
                dispatch(BookingActions.recordPreselectedCode(queryStringParams.get(promoCodeParam)));

            if (queryStringParams.get(gameParam)) dispatch(GameActions.preselectGame(queryStringParams.get(gameParam)));

            if (queryStringParams.get(priceSetParam))
                dispatch(BookingActions.recordPriceSet(params.venue, queryStringParams.get(priceSetParam), 'querystring'));

            setQueryAdded(true);
        }
    }, [dispatch, queryStringParams, queryAdded, params]);

    useEffect(() => {
        dispatch(PaymentActions.clearClientSecret());
    }, [dispatch]);

    //TODO see if this can be centralised / done at a higher level insted of duplicating between tickets page and global gifts page
    //Depends on accessCode so a code that arrives after the first load (a client-side
    //navigation into a deep link) can still reveal its hidden venue. loadSites() is guarded
    //internally - see needsPreviewReload in store/Sites.ts - so re-running it is a no-op
    //unless the preselected venue is missing from the list we already have.
    useEffect(() => {
        dispatch(SiteActions.loadSites());
    }, [dispatch, accessCode]);

    useEffect(() => {
        if (site?.siteId) {
            dispatch(RewardActions.loadRewardFlows(site.siteId));
        }
    }, [dispatch, site?.siteId]);

    useEffect(() => {
        // TODO - this seems like trouble, I don't understand what it should be doing and why
        if (giftVoucher && giftVoucherStatus !== GiftVoucherStatus.Pending) {
            dispatch(GiftVoucherActions.clearGiftVoucher());
            dispatch(PurchaseTypeActions.clearPurchaseType());
        }
    }, [dispatch, giftVoucherStatus, selectedPurchaseType, giftVoucher]);

    const priceSet = priceSets?.find((p) => p.venue === site?.urlKey);
    

    const hideBookingSection = !(
        (selectedPurchaseType === PurchaseTypes.Booking || selectedPurchaseType === PurchaseTypes.PrivateEvent) &&
        siteSelectedUI
    );

    const privateEventTypeSection =
        selectedPurchaseType === PurchaseTypes.PrivateEvent ? (
            <PrivateEventTypeSection active={selectedPurchaseType === PurchaseTypes.PrivateEvent} scrollTo={true} />
        ) : undefined;

    return (
        <React.Fragment>

        {/*new location picker widget*/}
            <Row justify="center" align="top">
                <Col xs={24} lg={24}>
                    <StickyInfoWidget 
                        onWidgetHeightChange={setLocationWidgetHeight} 
                        offsetTop={reservationTimerHeight}
                    />
                </Col>
            </Row>

            <Row justify="start" align="top" style={!showUiSections ? { display: 'none' } : {}}>
                <Col xs={24} sm={24} md={24} lg={18}>
                    <Heading level={HeadingSize.One} className="heading-indent" >
                        {t('pageTitles.selectTickets')}
                    </Heading>
                </Col>
                <Col xs={24} sm={24} md={24} lg={18}>

                    <BookingAlert siteAlert={site?.bookingAlert} priceSet={priceSet} />
                    <PurchaseTypeSection active={siteSelectedUI} scrollTo={true} />
                    <div hidden={hideBookingSection}>
                        {privateEventTypeSection}
                        <PlayerPickerSection
                            active={
                                (purchaseSelectedUI ?? false) &&
                                (selectedPurchaseType === PurchaseTypes.Booking ||
                                    selectedPurchaseType === PurchaseTypes.PrivateEvent)
                            }
                            scrollTo={selectedPurchaseType === PurchaseTypes.Booking}
                        />
                        <DatePickerSection active={(playersSelectedUI && (datesLoading || datesLoaded)) ?? false} scrollTo={false} />
                        <TimePickerSection active={(dateSelectedUI && (timesLoading || timesLoaded)) ?? false} scrollTo={true} />
                        <PackagePickerSection active={timeSelectedUI ?? false} scrollTo={true} />
                    </div>
                    <div hidden={!giftVoucherAvailable}>
                        <GiftVoucherAmountSection
                            active={(purchaseSelectedUI ?? false) && selectedPurchaseType === PurchaseTypes.GiftVoucher}
                            scrollTo={true}
                        />
                        <GiftVoucherDetailsSection active={purchaseAmountSelectedUI ?? false} scrollTo={true} />

                    </div>
                </Col>
                {/* BookingSummary - sidebar or bottom drawer depending on screen size */}
                <Col xs={24} sm={24} md={24} lg={6}>
                    <BookingSummary
                        drawer={isMobile} 
                        affix={!isMobile} 
                        dynamicOffsetTop={locationWidgetHeight}
                    />
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default TicketsPage;
